import { computed, DestroyRef, effect, inject, Injectable, Injector, signal, Signal, untracked } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { catchError, forkJoin, of, skip, Subject, switchMap } from 'rxjs';
import { IAlert, MDAlertService } from '@melodic-ui/components';
import { LessonV2Service } from '@lb/api/mylb/services';
import { ILessonListItem, IPagedResponse } from '@lb/api/mylb/interfaces';
import { LessonStateService } from '@lb/state/lesson-state.service';
import { searchFields } from '@lb/components/data-grid/filters/search-fields';
import { NestedKeyOf } from '@lb/components/data-grid/interfaces/ifilter-item.interface';

export type LessonListTab = 'Upcoming' | 'Previous';

export interface ILessonListConfig {
	/** Which side of the lesson the viewer is on — selects the learnerID vs instructorID filter. */
	userType: 'learner' | 'instructor';
	/** The user whose lessons are listed. Changing it resets to page 1 and reloads. */
	userID: Signal<number | null | undefined>;
	/** Fields {@link filteredItems} matches the search box against. Omit to disable searching. */
	searchFields?: NestedKeyOf<ILessonListItem>[];
	/** Optional client-side reordering applied to each fetched page (e.g. needs-attention first). */
	sort?: (lessons: ILessonListItem[]) => ILessonListItem[];
	/** Called with every freshly loaded page — e.g. to warm user summaries for the cards. */
	onItemsLoaded?: (lessons: ILessonListItem[]) => void;
	/** Page sizes offered by the paginator. The first entry becomes the initial size. */
	pageSizeOptions?: number[];
}

/**
 * Paged upcoming/prior lesson list shared by the learner and instructor "My Lessons" pages.
 *
 * Provide it on the component (`providers: [LessonListService]`) — it is per-list state, not a
 * singleton — then call {@link configure} from the constructor.
 *
 * Two things it deliberately does differently from the hand-rolled versions it replaces:
 *
 * - **One page size signal.** {@link pageSize} is the only page-size state; bind it straight to the
 *   paginator's `itemsPerPage`. The old pages carried a second `selectedPageLength` signal that the
 *   template bound but no handler ever wrote, so picking 50 kept paging as if it were 10.
 * - **Only the visible tab is fetched.** The counts behind both tab badges come from the count
 *   endpoints, so paging "Upcoming" no longer re-pages the hidden "Prior" list. Switching tabs now
 *   costs a fetch, which is the trade for halving the requests on every page change.
 */
@Injectable()
export class LessonListService {
	private readonly _lessonV2Service = inject(LessonV2Service);
	private readonly _lessonStateService = inject(LessonStateService);
	private readonly _alertService = inject(MDAlertService);
	private readonly _injector = inject(Injector);
	private readonly _destroyRef = inject(DestroyRef);

	private _config: ILessonListConfig | null = null;
	private readonly _loadPage$ = new Subject<void>();
	private readonly _loadCounts$ = new Subject<void>();

	readonly displayType = signal<LessonListTab>('Upcoming');

	readonly pageSizeOptions = signal<number[]>([10, 20, 50]);
	readonly page = signal<number>(1);
	readonly pageSize = signal<number>(10);

	readonly searchText = signal<string | null>(null);

	readonly items = signal<ILessonListItem[]>([]);
	readonly upcomingCount = signal<number>(0);
	readonly pastCount = signal<number>(0);

	readonly loading = signal<boolean>(false);
	readonly loaded = signal<boolean>(false);

	/** Server-side total for the active tab — the number the paginator pages over. */
	readonly totalCount = computed(() => (this.displayType() === 'Upcoming' ? this.upcomingCount() : this.pastCount()));

	/**
	 * Whether to show the paginator at all — true once there are more rows than the *smallest*
	 * offered page size, not more than the current one. Gating on the current size would hide the
	 * whole control after picking a size that fits everything (50 with 40 rows), stranding the user
	 * with no way back to 10.
	 */
	readonly showPaginator = computed(() => this.totalCount() > Math.min(...this.pageSizeOptions()));

	/**
	 * Search applied to the loaded page only. The lesson API has no search term (neither
	 * `ILessonFilterOption` nor the backend `LessonFilterOptions` carries one), so this cannot see
	 * rows on other pages and the count/paginator still reflect the unsearched total. Known gap —
	 * fixing it properly needs a `SearchTerm` on the server-side filter.
	 */
	readonly filteredItems = computed(() => {
		const items = this.items();
		const search = this.searchText();
		const fields = this._config?.searchFields;

		if (!search || search.length === 0 || fields == null || fields.length === 0) {
			return items;
		}

		return searchFields(search, items, fields);
	});

	configure(config: ILessonListConfig): void {
		// Wiring twice would double every fetch, so a second call is a no-op rather than a subtle
		// double-load. Call it once, from the host component's constructor.
		if (this._config != null) {
			return;
		}

		this._config = config;

		if (config.pageSizeOptions && config.pageSizeOptions.length > 0) {
			this.pageSizeOptions.set(config.pageSizeOptions);
			this.pageSize.set(config.pageSizeOptions[0]);
		}

		this._wirePageFetch();
		this._wireCountFetch();

		// Start over whenever the listed user changes (e.g. a guardian switching learners).
		effect(
			() => {
				const userID = config.userID();

				if (!userID) {
					return;
				}

				// untracked: these writes must not make the effect depend on its own output.
				untracked(() => {
					this.page.set(1);
					this.displayType.set('Upcoming');
					this._loadCounts$.next();
					this._loadPage$.next();
				});
			},
			{ injector: this._injector }
		);

		// Another component mutated a lesson (reschedule, attendance) — page and counts both stale.
		toObservable(this._lessonStateService.lessonChanged, { injector: this._injector })
			.pipe(skip(1), takeUntilDestroyed(this._destroyRef))
			.subscribe(() => this.reload());
	}

	goToPage(page: number): void {
		if (page === this.page()) {
			return;
		}

		this.page.set(page);
		this._loadPage$.next();
	}

	setPageSize(size: number): void {
		if (!size || size === this.pageSize()) {
			return;
		}

		this.pageSize.set(size);
		this.page.set(1);
		this._loadPage$.next();
	}

	setDisplayType(tab: LessonListTab): void {
		if (tab === this.displayType()) {
			return;
		}

		this.displayType.set(tab);
		this.page.set(1);
		this._loadPage$.next();
	}

	setSearchText(value: string | null): void {
		this.searchText.set(value);
	}

	/** Refetch the active page and both tab counts. Use after a lesson mutation. */
	reload(): void {
		this._loadCounts$.next();
		this._loadPage$.next();
	}

	private _wirePageFetch(): void {
		this._loadPage$
			.pipe(
				// switchMap, not concat: clicking through pages quickly must not let a slow earlier
				// response land after a newer one and leave the list showing the wrong page.
				switchMap(() => {
					const config = this._config;
					const userID = config?.userID();

					if (config == null || !userID) {
						return of(null);
					}

					this.loading.set(true);

					const request =
						this.displayType() === 'Upcoming'
							? this._lessonV2Service.getUpcoming(userID, config.userType, this.page(), this.pageSize())
							: this._lessonV2Service.getPast(userID, config.userType, this.page(), this.pageSize());

					return request.pipe(
						catchError(() => {
							this._alertService.addAlert({
								type: 'error',
								title: 'Failed to load lessons. Please try again later.',
								duration: 5000
							} as IAlert);

							return of(null);
						})
					);
				}),
				takeUntilDestroyed(this._destroyRef)
			)
			.subscribe((response: IPagedResponse<ILessonListItem> | null) => {
				if (response == null) {
					this.loading.set(false);
					this.loaded.set(true);
					return;
				}

				// The list we just fetched is the freshest source for its own tab's badge, so trust
				// it over whatever the count sweep last wrote.
				if (this.displayType() === 'Upcoming') {
					this.upcomingCount.set(response.totalCount);
				} else {
					this.pastCount.set(response.totalCount);
				}

				// A mutation can shrink the list under our feet — a cancelled lesson moves from
				// Upcoming to Prior — leaving `page` past the end and the page empty. The paginator
				// runs in controlled mode here and so deliberately won't self-correct, which makes
				// clamping the page the job of whoever owns it: us. Terminates because the refetched
				// page is within range.
				const lastPage = Math.max(1, Math.ceil(response.totalCount / this.pageSize()));

				if (this.page() > lastPage) {
					this.page.set(lastPage);
					this._loadPage$.next();
					return;
				}

				this.loading.set(false);
				this.loaded.set(true);

				const config = this._config;
				const items = config?.sort ? config.sort(response.items) : response.items;

				this.items.set(items);

				config?.onItemsLoaded?.(items);
			});
	}

	private _wireCountFetch(): void {
		this._loadCounts$
			.pipe(
				switchMap(() => {
					const config = this._config;
					const userID = config?.userID();

					if (config == null || !userID) {
						return of(null);
					}

					// Counts only move on mutation or a user change, so this runs then — not on
					// every page or tab change. A failure here only stales a badge, so it stays
					// silent rather than firing an alert over the list's own error.
					return forkJoin([
						this._lessonV2Service.getUpcomingCount(userID, config.userType),
						this._lessonV2Service.getPastCount(userID, config.userType)
					]).pipe(catchError(() => of(null)));
				}),
				takeUntilDestroyed(this._destroyRef)
			)
			.subscribe((counts) => {
				if (counts == null) {
					return;
				}

				this.upcomingCount.set(counts[0]);
				this.pastCount.set(counts[1]);
			});
	}
}
